import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { 
  Calendar as CalendarIcon, 
  Users, 
  DollarSign, 
  Mail, 
  Plus, 
  Trash2,
  Clock,
  CheckCircle,
  XCircle,
  Settings,
  RefreshCw,
  ChevronLeft,
  ChevronRight
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DAY_NAMES = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const ALL_TIMES = ["08:00","09:00","10:00","11:00","12:00","13:00","14:00","15:00","16:00","17:00","18:00","19:00","20:00","21:00"];

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function CalendarView({ bookings, services, blockedDates, calendarMonth, setCalendarMonth, selectedCalDate, setSelectedCalDate, formatDate, getPaymentBadge, handleCancelBooking }) {
  const year = calendarMonth.getFullYear();
  const month = calendarMonth.getMonth();

  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  
  // Monday = 0, Sunday = 6
  let startDow = firstDay.getDay() - 1;
  if (startDow < 0) startDow = 6;

  const daysInMonth = lastDay.getDate();
  const today = new Date().toISOString().split("T")[0];

  // Build booking counts per date
  const bookingsByDate = {};
  const paidBookings = bookings.filter(b => b.payment_status === "paid");
  paidBookings.forEach(b => {
    if (!bookingsByDate[b.date]) bookingsByDate[b.date] = [];
    bookingsByDate[b.date].push(b);
  });

  const blockedSet = new Set(blockedDates.map(d => d.date));

  const prevMonth = () => setCalendarMonth(new Date(year, month - 1, 1));
  const nextMonth = () => setCalendarMonth(new Date(year, month + 1, 1));
  const monthLabel = calendarMonth.toLocaleDateString("en-GB", { month: "long", year: "numeric" });

  // Selected date bookings (all statuses)
  const selectedBookings = bookings.filter(b => b.date === selectedCalDate);

  // Service name lookup
  const serviceMap = {};
  services.forEach(s => { serviceMap[s.id] = s; });

  const cells = [];
  for (let i = 0; i < startDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
      {/* Calendar Grid */}
      <div className="lg:col-span-2">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-serif text-2xl">Booking Calendar</h2>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="icon" className="rounded-none h-9 w-9" onClick={prevMonth} data-testid="cal-prev-month">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="text-sm font-medium min-w-[160px] text-center" data-testid="cal-month-label">{monthLabel}</span>
            <Button variant="outline" size="icon" className="rounded-none h-9 w-9" onClick={nextMonth} data-testid="cal-next-month">
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Legend */}
        <div className="flex flex-wrap gap-4 mb-4 text-xs text-muted-foreground">
          <span className="flex items-center gap-1.5"><span className="w-3 h-3 bg-primary/20 border border-primary/40" /> Bookings</span>
          <span className="flex items-center gap-1.5"><span className="w-3 h-3 bg-destructive/20 border border-destructive/40" /> Blocked</span>
          <span className="flex items-center gap-1.5"><span className="w-3 h-3 bg-primary border border-primary" /> Today</span>
        </div>

        {/* Weekday headers */}
        <div className="grid grid-cols-7 gap-1 mb-1">
          {WEEKDAYS.map(d => (
            <div key={d} className="text-center text-xs font-medium text-muted-foreground py-2">{d}</div>
          ))}
        </div>

        {/* Day cells */}
        <div className="grid grid-cols-7 gap-1" data-testid="cal-grid">
          {cells.map((day, i) => {
            if (day === null) return <div key={`empty-${i}`} className="aspect-square" />;

            const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
            const dayBookings = bookingsByDate[dateStr] || [];
            const isBlocked = blockedSet.has(dateStr);
            const isToday = dateStr === today;
            const isSelected = dateStr === selectedCalDate;
            const isPast = dateStr < today;

            return (
              <button
                key={dateStr}
                onClick={() => setSelectedCalDate(dateStr)}
                className={`aspect-square border relative flex flex-col items-center justify-start pt-1.5 transition-all text-sm
                  ${isSelected ? "border-primary bg-primary/5 ring-1 ring-primary" : "border-border hover:border-primary/40"}
                  ${isBlocked ? "bg-destructive/5 border-destructive/30" : ""}
                  ${isPast ? "opacity-50" : ""}
                `}
                data-testid={`cal-day-${dateStr}`}
              >
                <span className={`text-xs font-medium leading-none ${isToday ? "bg-primary text-primary-foreground w-6 h-6 flex items-center justify-center" : ""}`}>
                  {day}
                </span>
                {dayBookings.length > 0 && (
                  <div className="mt-1 flex flex-col items-center gap-0.5">
                    <span className="text-[10px] font-semibold text-primary">{dayBookings.length}</span>
                    <div className="flex gap-0.5">
                      {dayBookings.slice(0, 3).map((_, idx) => (
                        <span key={idx} className="w-1.5 h-1.5 bg-primary rounded-full" />
                      ))}
                      {dayBookings.length > 3 && <span className="text-[8px] text-primary">+</span>}
                    </div>
                  </div>
                )}
                {isBlocked && <span className="text-[9px] text-destructive mt-0.5">Blocked</span>}
              </button>
            );
          })}
        </div>
      </div>

      {/* Selected Date Detail */}
      <div>
        <h3 className="font-serif text-xl mb-2" data-testid="cal-selected-date-title">
          {new Date(selectedCalDate + "T00:00:00").toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" })}
        </h3>
        <p className="text-xs text-muted-foreground mb-6">
          {selectedBookings.length} booking{selectedBookings.length !== 1 ? "s" : ""}
          {blockedSet.has(selectedCalDate) && <span className="text-destructive ml-2">(Blocked)</span>}
        </p>

        {selectedBookings.length === 0 ? (
          <div className="bg-muted border border-border p-8 text-center">
            <CalendarIcon className="w-8 h-8 text-muted-foreground/40 mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">No bookings on this date</p>
          </div>
        ) : (
          <div className="space-y-3">
            {selectedBookings
              .sort((a, b) => a.time.localeCompare(b.time))
              .map((booking) => (
                <div
                  key={booking.id}
                  className="bg-muted border border-border p-4"
                  data-testid={`cal-booking-${booking.id}`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <span className="font-serif text-lg leading-tight">{booking.time}</span>
                    {getPaymentBadge(booking.payment_status)}
                  </div>
                  <p className="text-sm font-medium">{booking.customer_name}</p>
                  <p className="text-xs text-muted-foreground">{booking.service_name}</p>
                  <p className="text-xs text-muted-foreground">{booking.customer_email}</p>
                  <div className="flex items-center justify-between mt-3 pt-3 border-t border-border">
                    <span className="font-serif text-base">£{booking.price.toFixed(0)}</span>
                    {booking.payment_status !== "cancelled" && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="rounded-none border-destructive text-destructive hover:bg-destructive hover:text-destructive-foreground text-xs h-7 px-3"
                        onClick={() => handleCancelBooking(booking.id)}
                      >
                        Cancel
                      </Button>
                    )}
                  </div>
                </div>
              ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function AdminPage() {
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [services, setServices] = useState([]);
  const [blockedDates, setBlockedDates] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);

  // Working hours state
  const [workingHours, setWorkingHours] = useState({
    weekday_times: ["09:00", "10:00", "11:00", "12:00", "13:00", "14:00", "15:00", "16:00", "17:00", "18:00", "19:00", "20:00"],
    sway_times: ["10:00", "14:00"],
    sway_days: [5],
    days_off: [],
  });

  // Form states
  const [newBlockedDate, setNewBlockedDate] = useState(null);
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [selectedCalDate, setSelectedCalDate] = useState(new Date().toISOString().split("T")[0]);
  const [newSlot, setNewSlot] = useState({
    serviceId: "",
    date: "",
    time: "",
    spots: 1,
  });

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setIsLoading(true);
    try {
      const [statsRes, bookingsRes, servicesRes, blockedRes, subscribersRes, hoursRes] = await Promise.all([
        axios.get(`${API}/admin/stats`),
        axios.get(`${API}/bookings`),
        axios.get(`${API}/services`),
        axios.get(`${API}/blocked-dates`),
        axios.get(`${API}/newsletter/subscribers`),
        axios.get(`${API}/admin/settings/working-hours`),
      ]);

      setStats(statsRes.data);
      setBookings(bookingsRes.data);
      setServices(servicesRes.data);
      setBlockedDates(blockedRes.data);
      setSubscribers(subscribersRes.data);
      setWorkingHours(hoursRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load admin data");
    } finally {
      setIsLoading(false);
    }
  };

  const handleBlockDate = async () => {
    if (!newBlockedDate) {
      toast.error("Please select a date to block");
      return;
    }

    try {
      const dateStr = newBlockedDate.toISOString().split("T")[0];
      await axios.post(`${API}/blocked-dates?date=${dateStr}`);
      toast.success("Date blocked successfully");
      setNewBlockedDate(null);
      fetchAllData();
    } catch (error) {
      toast.error("Failed to block date");
    }
  };

  const handleUnblockDate = async (date) => {
    try {
      await axios.delete(`${API}/blocked-dates/${date}`);
      toast.success("Date unblocked");
      fetchAllData();
    } catch (error) {
      toast.error("Failed to unblock date");
    }
  };

  const handleCreateSlot = async () => {
    if (!newSlot.serviceId || !newSlot.date || !newSlot.time) {
      toast.error("Please fill in all fields");
      return;
    }

    try {
      await axios.post(`${API}/timeslots`, {
        service_id: newSlot.serviceId,
        date: newSlot.date,
        time: newSlot.time,
        available_spots: parseInt(newSlot.spots),
      });
      toast.success("Time slot created");
      setNewSlot({ serviceId: "", date: "", time: "", spots: 1 });
      fetchAllData();
    } catch (error) {
      toast.error("Failed to create time slot");
    }
  };

  const handleCancelBooking = async (bookingId) => {
    try {
      await axios.put(`${API}/bookings/${bookingId}/cancel`);
      toast.success("Booking cancelled");
      fetchAllData();
    } catch (error) {
      toast.error("Failed to cancel booking");
    }
  };

  const handleSaveWorkingHours = async () => {
    try {
      await axios.put(`${API}/admin/settings/working-hours`, workingHours);
      toast.success("Working hours saved! New slots will be generated automatically.");
    } catch (error) {
      toast.error("Failed to save working hours");
    }
  };

  const handleGenerateSlots = async () => {
    setIsGenerating(true);
    try {
      await axios.post(`${API}/admin/generate-slots`);
      toast.success("Time slots generated for the next 60 days!");
      fetchAllData();
    } catch (error) {
      toast.error("Failed to generate slots");
    } finally {
      setIsGenerating(false);
    }
  };

  const toggleTime = (timeList, time, key) => {
    setWorkingHours(prev => ({
      ...prev,
      [key]: prev[key].includes(time)
        ? prev[key].filter(t => t !== time)
        : [...prev[key], time].sort()
    }));
  };

  const toggleDay = (dayIndex, key) => {
    setWorkingHours(prev => ({
      ...prev,
      [key]: prev[key].includes(dayIndex)
        ? prev[key].filter(d => d !== dayIndex)
        : [...prev[key], dayIndex].sort()
    }));
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const getPaymentBadge = (paymentStatus) => {
    if (paymentStatus === "paid") {
      return (
        <span className="flex items-center gap-1 text-xs text-green-600 bg-green-100 px-2 py-1">
          <CheckCircle className="w-3 h-3" /> Paid
        </span>
      );
    }
    if (paymentStatus === "cancelled") {
      return (
        <span className="flex items-center gap-1 text-xs text-red-600 bg-red-100 px-2 py-1">
          <XCircle className="w-3 h-3" /> Cancelled
        </span>
      );
    }
    return (
      <span className="flex items-center gap-1 text-xs text-yellow-600 bg-yellow-100 px-2 py-1">
        <Clock className="w-3 h-3" /> Pending
      </span>
    );
  };

  if (isLoading) {
    return (
      <div className="min-h-screen pt-32 pb-24 flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-32 pb-24" data-testid="admin-page">
      <div className="container-custom">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="mb-12"
        >
          <h1 className="heading-lg text-foreground mb-2">
            Admin <em className="italic">Dashboard</em>
          </h1>
          <p className="body-text text-muted-foreground">
            Manage your bookings, services, and availability.
          </p>
        </motion.div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
            <div className="bg-muted p-6 border border-border" data-testid="stat-bookings">
              <Users className="w-6 h-6 text-primary mb-3" />
              <p className="text-3xl font-serif text-foreground">{stats.total_bookings}</p>
              <p className="text-sm text-muted-foreground">Total Bookings</p>
            </div>
            <div className="bg-muted p-6 border border-border" data-testid="stat-revenue">
              <DollarSign className="w-6 h-6 text-primary mb-3" />
              <p className="text-3xl font-serif text-foreground">£{stats.total_revenue.toFixed(0)}</p>
              <p className="text-sm text-muted-foreground">Revenue</p>
            </div>
            <div className="bg-muted p-6 border border-border" data-testid="stat-subscribers">
              <Mail className="w-6 h-6 text-primary mb-3" />
              <p className="text-3xl font-serif text-foreground">{stats.newsletter_subscribers}</p>
              <p className="text-sm text-muted-foreground">Subscribers</p>
            </div>
            <div className="bg-muted p-6 border border-border" data-testid="stat-vouchers">
              <CalendarIcon className="w-6 h-6 text-primary mb-3" />
              <p className="text-3xl font-serif text-foreground">{stats.vouchers_sold}</p>
              <p className="text-sm text-muted-foreground">Vouchers Sold</p>
            </div>
          </div>
        )}

        {/* Tabs */}
        <Tabs defaultValue="bookings" className="space-y-8">
          <TabsList className="bg-muted border border-border rounded-none p-1 flex-wrap">
            <TabsTrigger value="bookings" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              Bookings
            </TabsTrigger>
            <TabsTrigger value="calendar" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground" data-testid="calendar-tab-trigger">
              Calendar
            </TabsTrigger>
            <TabsTrigger value="availability" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              Availability
            </TabsTrigger>
            <TabsTrigger value="settings" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              Settings
            </TabsTrigger>
            <TabsTrigger value="subscribers" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              Subscribers
            </TabsTrigger>
          </TabsList>

          {/* Bookings Tab */}
          <TabsContent value="bookings" data-testid="bookings-tab">
            <div className="space-y-4">
              <h2 className="font-serif text-2xl">Recent Bookings</h2>
              {bookings.length === 0 ? (
                <p className="text-muted-foreground py-8 text-center">No bookings yet.</p>
              ) : (
                <div className="space-y-3">
                  {bookings.map((booking) => (
                    <div
                      key={booking.id}
                      className="bg-muted p-6 border border-border flex flex-col md:flex-row md:items-center justify-between gap-4"
                      data-testid={`booking-${booking.id}`}
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="font-serif text-lg">{booking.customer_name}</span>
                          {getPaymentBadge(booking.payment_status)}
                        </div>
                        <p className="text-sm text-muted-foreground">{booking.service_name}</p>
                        <p className="text-sm text-muted-foreground">
                          {formatDate(booking.date)} at {booking.time}
                        </p>
                        <p className="text-sm text-muted-foreground">{booking.customer_email}</p>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className="font-serif text-xl">£{booking.price.toFixed(0)}</span>
                        {booking.payment_status !== "cancelled" && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="rounded-none border-destructive text-destructive hover:bg-destructive hover:text-destructive-foreground"
                            onClick={() => handleCancelBooking(booking.id)}
                            data-testid={`cancel-booking-${booking.id}`}
                          >
                            Cancel
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Calendar Tab */}
          <TabsContent value="calendar" data-testid="calendar-tab">
            <CalendarView
              bookings={bookings}
              services={services}
              blockedDates={blockedDates}
              calendarMonth={calendarMonth}
              setCalendarMonth={setCalendarMonth}
              selectedCalDate={selectedCalDate}
              setSelectedCalDate={setSelectedCalDate}
              formatDate={formatDate}
              getPaymentBadge={getPaymentBadge}
              handleCancelBooking={handleCancelBooking}
            />
          </TabsContent>

          {/* Availability Tab */}
          <TabsContent value="availability" data-testid="availability-tab">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              {/* Block Dates */}
              <div>
                <h2 className="font-serif text-2xl mb-6">Block Dates</h2>
                <div className="flex justify-center mb-6">
                  <Calendar
                    mode="single"
                    selected={newBlockedDate}
                    onSelect={setNewBlockedDate}
                    className="rounded-none border border-border"
                    data-testid="block-date-calendar"
                  />
                </div>
                <Button
                  onClick={handleBlockDate}
                  disabled={!newBlockedDate}
                  className="w-full rounded-none bg-primary hover:bg-primary/90 text-primary-foreground"
                  data-testid="block-date-btn"
                >
                  <Plus className="mr-2 w-4 h-4" />
                  Block Selected Date
                </Button>

                {blockedDates.length > 0 && (
                  <div className="mt-6">
                    <h3 className="text-sm font-medium mb-3">Blocked Dates:</h3>
                    <div className="space-y-2">
                      {blockedDates.map((blocked) => (
                        <div
                          key={blocked.date}
                          className="flex items-center justify-between bg-muted p-3 border border-border"
                        >
                          <span className="text-sm">{formatDate(blocked.date)}</span>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleUnblockDate(blocked.date)}
                            className="text-destructive hover:text-destructive"
                            data-testid={`unblock-${blocked.date}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Generate Slots + Create Individual */}
              <div>
                <div className="mb-8">
                  <h2 className="font-serif text-2xl mb-4">Auto-Generate Slots</h2>
                  <p className="text-sm text-muted-foreground mb-4">
                    Generate time slots for the next 60 days based on your working hours settings. 
                    This runs automatically every hour, but you can trigger it manually here.
                  </p>
                  <Button
                    onClick={handleGenerateSlots}
                    disabled={isGenerating}
                    className="w-full rounded-none bg-primary hover:bg-primary/90 text-primary-foreground"
                    data-testid="generate-slots-btn"
                  >
                    <RefreshCw className={`mr-2 w-4 h-4 ${isGenerating ? "animate-spin" : ""}`} />
                    {isGenerating ? "Generating..." : "Generate Slots Now"}
                  </Button>
                </div>

                <h2 className="font-serif text-2xl mb-6">Create Individual Slot</h2>
                <div className="space-y-4">
                  <div>
                    <Label className="text-sm mb-2 block">Service</Label>
                    <Select
                      value={newSlot.serviceId}
                      onValueChange={(value) => setNewSlot(prev => ({ ...prev, serviceId: value }))}
                    >
                      <SelectTrigger className="rounded-none" data-testid="service-select">
                        <SelectValue placeholder="Select service" />
                      </SelectTrigger>
                      <SelectContent>
                        {services.map((service) => (
                          <SelectItem key={service.id} value={service.id}>
                            {service.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label className="text-sm mb-2 block">Date</Label>
                    <Input
                      type="date"
                      value={newSlot.date}
                      onChange={(e) => setNewSlot(prev => ({ ...prev, date: e.target.value }))}
                      className="rounded-none border-border"
                      data-testid="slot-date-input"
                    />
                  </div>

                  <div>
                    <Label className="text-sm mb-2 block">Time</Label>
                    <Input
                      type="time"
                      value={newSlot.time}
                      onChange={(e) => setNewSlot(prev => ({ ...prev, time: e.target.value }))}
                      className="rounded-none border-border"
                      data-testid="slot-time-input"
                    />
                  </div>

                  <div>
                    <Label className="text-sm mb-2 block">Available Spots</Label>
                    <Input
                      type="number"
                      min="1"
                      value={newSlot.spots}
                      onChange={(e) => setNewSlot(prev => ({ ...prev, spots: e.target.value }))}
                      className="rounded-none border-border"
                      data-testid="slot-spots-input"
                    />
                  </div>

                  <Button
                    onClick={handleCreateSlot}
                    className="w-full rounded-none bg-primary hover:bg-primary/90 text-primary-foreground"
                    data-testid="create-slot-btn"
                  >
                    <Plus className="mr-2 w-4 h-4" />
                    Create Time Slot
                  </Button>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings" data-testid="settings-tab">
            <div className="max-w-3xl">
              <div className="flex items-center gap-3 mb-8">
                <Settings className="w-6 h-6 text-primary" />
                <h2 className="font-serif text-2xl">Working Hours & Schedule</h2>
              </div>

              {/* 1:1 Session Times */}
              <div className="mb-10">
                <h3 className="font-serif text-xl mb-2">1:1 Session Times</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Select the times you're available for 1:1 treatments (massage, Reiki, sound healing).
                </p>
                <div className="flex flex-wrap gap-2">
                  {ALL_TIMES.map((time) => (
                    <button
                      key={`weekday-${time}`}
                      onClick={() => toggleTime(workingHours.weekday_times, time, "weekday_times")}
                      className={`px-4 py-2 text-sm border transition-colors ${
                        workingHours.weekday_times.includes(time)
                          ? "bg-primary text-primary-foreground border-primary"
                          : "bg-background text-muted-foreground border-border hover:border-primary/50"
                      }`}
                      data-testid={`weekday-time-${time}`}
                    >
                      {time}
                    </button>
                  ))}
                </div>
              </div>

              {/* Sway with Sound Times */}
              <div className="mb-10">
                <h3 className="font-serif text-xl mb-2">Sway with Sound Times</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Select the times for group Sway with Sound classes.
                </p>
                <div className="flex flex-wrap gap-2">
                  {ALL_TIMES.map((time) => (
                    <button
                      key={`sway-${time}`}
                      onClick={() => toggleTime(workingHours.sway_times, time, "sway_times")}
                      className={`px-4 py-2 text-sm border transition-colors ${
                        workingHours.sway_times.includes(time)
                          ? "bg-primary text-primary-foreground border-primary"
                          : "bg-background text-muted-foreground border-border hover:border-primary/50"
                      }`}
                      data-testid={`sway-time-${time}`}
                    >
                      {time}
                    </button>
                  ))}
                </div>
              </div>

              {/* Sway Days */}
              <div className="mb-10">
                <h3 className="font-serif text-xl mb-2">Sway with Sound Days</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Which days do you run Sway with Sound group classes?
                </p>
                <div className="flex flex-wrap gap-2">
                  {DAY_NAMES.map((day, index) => (
                    <button
                      key={`sway-day-${index}`}
                      onClick={() => toggleDay(index, "sway_days")}
                      className={`px-4 py-2 text-sm border transition-colors ${
                        workingHours.sway_days.includes(index)
                          ? "bg-primary text-primary-foreground border-primary"
                          : "bg-background text-muted-foreground border-border hover:border-primary/50"
                      }`}
                      data-testid={`sway-day-${index}`}
                    >
                      {day}
                    </button>
                  ))}
                </div>
              </div>

              {/* Days Off */}
              <div className="mb-10">
                <h3 className="font-serif text-xl mb-2">Days Off</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Select your regular days off (no bookings on these days).
                </p>
                <div className="flex flex-wrap gap-2">
                  {DAY_NAMES.map((day, index) => (
                    <button
                      key={`off-day-${index}`}
                      onClick={() => toggleDay(index, "days_off")}
                      className={`px-4 py-2 text-sm border transition-colors ${
                        workingHours.days_off.includes(index)
                          ? "bg-destructive/10 text-destructive border-destructive/30"
                          : "bg-background text-muted-foreground border-border hover:border-primary/50"
                      }`}
                      data-testid={`off-day-${index}`}
                    >
                      {day}
                    </button>
                  ))}
                </div>
              </div>

              {/* Save Button */}
              <div className="flex gap-4">
                <Button
                  onClick={handleSaveWorkingHours}
                  className="rounded-none bg-primary hover:bg-primary/90 text-primary-foreground px-12 py-6 text-xs tracking-[0.15em] uppercase"
                  data-testid="save-hours-btn"
                >
                  Save Working Hours
                </Button>
                <Button
                  onClick={handleGenerateSlots}
                  disabled={isGenerating}
                  variant="outline"
                  className="rounded-none border-primary text-primary px-12 py-6 text-xs tracking-[0.15em] uppercase"
                  data-testid="generate-after-save-btn"
                >
                  <RefreshCw className={`mr-2 w-4 h-4 ${isGenerating ? "animate-spin" : ""}`} />
                  {isGenerating ? "Generating..." : "Generate Slots Now"}
                </Button>
              </div>
            </div>
          </TabsContent>

          {/* Subscribers Tab */}
          <TabsContent value="subscribers" data-testid="subscribers-tab">
            <div>
              <h2 className="font-serif text-2xl mb-6">Newsletter Subscribers ({subscribers.length})</h2>
              {subscribers.length === 0 ? (
                <p className="text-muted-foreground py-8 text-center">No subscribers yet.</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {subscribers.map((sub) => (
                    <div
                      key={sub.id}
                      className="bg-muted p-4 border border-border"
                      data-testid={`subscriber-${sub.id}`}
                    >
                      <p className="text-sm font-medium">{sub.email}</p>
                      {sub.name && <p className="text-xs text-muted-foreground">{sub.name}</p>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
